import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface PopulationTrendsSectionProps {
  location: string;
  geofips?: string | null;
  embedOnly?: boolean;
}

interface CompositionPoint {
  year: number;
  race: string;
  count: number;
}

interface SexByYearPoint {
  year: number;
  sex: string;
  total_worker: number;
}

type PopulationTab = 'by_sex' | 'by_race' | 'by_race_pie';
type DataGeoType = 'City' | 'County' | 'State';

const tabTitleMap: Record<PopulationTab, string> = {
  by_sex: 'Workers by Sex',
  by_race: 'Householders by Race',
  by_race_pie: 'Race Pie Chart',
};

const raceOrder = [
  'White alone',
  'Black or African American alone',
  'Asian alone',
  'American Indian or Alaska Native alone',
  'Native Hawaiian or other Pacific Islander alone',
  'Some other race alone',
  'Two or more races',
];

const raceLabelMap: Record<string, string> = {
  'White alone': 'White',
  'Black or African American alone': 'Black',
  'Asian alone': 'Asian',
  'American Indian or Alaska Native alone': 'American Indian/Alaska Native',
  'Native Hawaiian or other Pacific Islander alone': 'Native Hawaiian/Pacific Islander',
  'Some other race alone': 'Some Other Race',
  'Two or more races': 'Two or More Races',
};

const raceColorMap: Record<string, string> = {
  'White alone': 'rgba(59, 130, 246, 1)',
  'Black or African American alone': 'rgba(16, 185, 129, 1)',
  'Asian alone': 'rgba(245, 158, 11, 1)',
  'American Indian or Alaska Native alone': 'rgba(239, 68, 68, 1)',
  'Native Hawaiian or other Pacific Islander alone': 'rgba(139, 92, 246, 1)',
  'Some other race alone': 'rgba(20, 184, 166, 1)',
  'Two or more races': 'rgba(236, 72, 153, 1)',
};

const PopulationTrendsSection: React.FC<PopulationTrendsSectionProps> = ({
  location,
  geofips,
  embedOnly = false,
}) => {
  const [activeTab, setActiveTab] = useState<PopulationTab>('by_sex');
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [chartData, setChartData] = useState<any>(null);
  const [sexSeries, setSexSeries] = useState<SexByYearPoint[]>([]);
  const [compositionSeries, setCompositionSeries] = useState<CompositionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingCountyData, setIsUsingCountyData] = useState(false);
  const [countyName, setCountyName] = useState<string | null>(null);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  /** Top N races by share for latest year (for pie chart key metrics). */
  const topRacesByShare = useMemo(() => {
    if (compositionSeries.length === 0) return [];
    const latestYear = compositionSeries.reduce((max, row) => Math.max(max, row.year), 0);
    const rows = compositionSeries.filter((row) => row.year === latestYear);
    const total = rows.reduce((sum, row) => sum + row.count, 0);
    if (!total) return [];
    return raceOrder
      .filter((race) => rows.some((r) => r.race === race))
      .map((race) => {
        const count = rows.filter((r) => r.race === race).reduce((s, r) => s + r.count, 0);
        return { race, label: raceLabelMap[race] || race, pct: (count / total) * 100 };
      })
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 3);
  }, [compositionSeries]);

  const resolveLocationForQuery = useCallback(async (): Promise<{ queryGeofips: number; geoType: DataGeoType }> => {
    let locationGeofips = geofips ? String(geofips) : null;
    if (!locationGeofips) {
      const geofipsNum = await supabaseLocationService.getGeofips(location);
      locationGeofips = geofipsNum ? String(geofipsNum) : null;
    }
    if (!locationGeofips) {
      throw new Error(`Could not find geofips for location: ${location}`);
    }

    const { data: locationInfo, error: locationError } = await supabaseClient
      .from('population')
      .select('geography_type, county')
      .eq('geofips', parseInt(locationGeofips, 10))
      .limit(1)
      .single();

    if (locationError || !locationInfo) {
      return { queryGeofips: parseInt(locationGeofips, 10), geoType: 'City' };
    }

    if (locationInfo.geography_type === 'County') {
      setIsUsingCountyData(false);
      setCountyName(null);
      return { queryGeofips: parseInt(locationGeofips, 10), geoType: 'County' };
    }

    if (locationInfo.geography_type === 'State') {
      setIsUsingCountyData(false);
      setCountyName(null);
      return { queryGeofips: 6, geoType: 'State' };
    }

    setIsUsingCountyData(false);
    setCountyName(null);
    return { queryGeofips: parseInt(locationGeofips, 10), geoType: 'City' };
  }, [geofips, location]);

  /** Resolve geofips and geography type for LODES tables (place/county/state). */
  const resolveLodesForQuery = useCallback(async (): Promise<{ queryGeofips: string; geographyType: 'place' | 'county' | 'state' }> => {
    let locationGeofips = geofips ? String(geofips) : null;
    if (!locationGeofips) {
      const geofipsNum = await supabaseLocationService.getGeofips(location);
      locationGeofips = geofipsNum ? String(geofipsNum) : null;
    }
    if (!locationGeofips) throw new Error(`Could not find geofips for location: ${location}`);

    const { data: locationInfo, error: locationError } = await supabaseClient
      .from('population')
      .select('geography_type, county')
      .eq('geofips', parseInt(locationGeofips, 10))
      .limit(1)
      .single();

    if (locationError || !locationInfo) {
      return { queryGeofips: locationGeofips, geographyType: 'place' };
    }
    if (locationInfo.geography_type === 'County') {
      return { queryGeofips: locationGeofips, geographyType: 'county' };
    }
    if (locationInfo.geography_type === 'State') {
      return { queryGeofips: '6', geographyType: 'state' };
    }
    // City: try place first
    const cityGeofips6 = String(parseInt(locationGeofips)).padStart(7, '0').slice(-6);
    const { data: placeRow } = await supabaseClient
      .from('lodes_employees_workplace_sex')
      .select('geofips')
      .eq('geofips', cityGeofips6)
      .eq('geography_type', 'place')
      .limit(1)
      .single();
    if (placeRow) {
      setIsUsingCountyData(false);
      setCountyName(null);
      return { queryGeofips: cityGeofips6, geographyType: 'place' };
    }
    const fallbackCountyName = locationInfo.county || `${location} County`;
    const { data: countyRow } = await supabaseClient
      .from('population')
      .select('geofips')
      .eq('location_name', fallbackCountyName)
      .eq('geography_type', 'County')
      .limit(1)
      .single();
    if (countyRow?.geofips) {
      setIsUsingCountyData(true);
      setCountyName(fallbackCountyName);
      return { queryGeofips: String(countyRow.geofips), geographyType: 'county' };
    }
    setIsUsingCountyData(false);
    setCountyName(null);
    return { queryGeofips: locationGeofips, geographyType: 'place' };
  }, [geofips, location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setChartData(null);
        setSexSeries([]);
        setCompositionSeries([]);

        if (activeTab === 'by_sex') {
          const { queryGeofips, geographyType } = await resolveLodesForQuery();
          const { data, error: sexError } = await supabaseClient
            .from('lodes_employees_workplace_sex')
            .select('year, sex, total_worker')
            .eq('geofips', queryGeofips)
            .eq('geography_type', geographyType)
            .gte('year', 2009)
            .order('year', { ascending: true });

          if (sexError) throw sexError;
          const rows = (data || []).map((row: any) => ({
            year: Number(row.year),
            sex: String(row.sex || ''),
            total_worker: Number(row.total_worker) || 0,
          }));
          if (rows.length === 0) {
            setLoading(false);
            return;
          }
          setSexSeries(rows);
          const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
          const sexValues = Array.from(new Set(rows.map((r) => r.sex))).filter(Boolean).sort();
          const colors = ['rgba(59, 130, 246, 1)', 'rgba(236, 72, 153, 1)', 'rgba(107, 114, 128, 1)'];
          const datasets = sexValues.map((sex, i) => ({
            label: sex,
            data: years.map((y) => rows.filter((r) => r.year === y && r.sex === sex).reduce((s, r) => s + r.total_worker, 0)),
            borderColor: colors[i % colors.length],
            backgroundColor: colors[i % colors.length].replace('1)', '0.15)'),
            borderWidth: 2,
            fill: true,
            tension: 0.2,
            pointRadius: 2,
            pointHoverRadius: 4,
          }));
          setChartData({ labels: years.map((y) => y.toString()), datasets });
        } else {
          const { queryGeofips, geoType } = await resolveLocationForQuery();
          const educationGeoType = geoType === 'City' ? 'place' : geoType === 'County' ? 'county' : 'state';
          const { data, error: compError } = await supabaseClient
            .from('educational_attain_race')
            .select('year, race, count, educ_level')
            .eq('geofips', queryGeofips)
            .eq('geography_type', educationGeoType)
            .eq('gender', 'Total')
            .in('race', raceOrder)
            .gte('year', 2015)
            .order('year', { ascending: true });

          if (compError) throw compError;

          const collapseByYearRace = (input: any[]): CompositionPoint[] => {
            // educational_attain_race can contain multiple education levels for a race/year.
            // Keep the largest count per race/year to avoid double counting overlapping cohorts.
            const byYearRace = new Map<string, CompositionPoint>();
            input.forEach((row: any) => {
              const point: CompositionPoint = {
                year: Number(row.year),
                race: String(row.race),
                count: Number(row.count) || 0,
              };
              const key = `${point.year}__${point.race}`;
              const existing = byYearRace.get(key);
              if (!existing || point.count > existing.count) {
                byYearRace.set(key, point);
              }
            });
            return Array.from(byYearRace.values());
          };

          let rows = collapseByYearRace(data || []);

          if (rows.length === 0 && educationGeoType === 'place') {
            const { data: cityInfo } = await supabaseClient
              .from('population')
              .select('county')
              .eq('geofips', queryGeofips)
              .eq('geography_type', 'City')
              .limit(1)
              .single();
            const fallbackCountyName = cityInfo?.county || `${location} County`;
            const { data: countyRow } = await supabaseClient
              .from('population')
              .select('geofips')
              .eq('location_name', fallbackCountyName)
              .eq('geography_type', 'County')
              .limit(1)
              .single();

            if (countyRow?.geofips) {
              const { data: countyCompRows, error: countyCompError } = await supabaseClient
                .from('educational_attain_race')
                .select('year, race, count, educ_level')
                .eq('geofips', countyRow.geofips)
                .eq('geography_type', 'county')
                .eq('gender', 'Total')
                .in('race', raceOrder)
                .gte('year', 2015)
                .order('year', { ascending: true });

              if (!countyCompError && countyCompRows?.length) {
                rows = collapseByYearRace(countyCompRows);
                setIsUsingCountyData(true);
                setCountyName(fallbackCountyName);
              }
            }
          }

          if (rows.length === 0) {
            setLoading(false);
            return;
          }

          const yearlyTotals = new Map<number, number>();
          rows.forEach((row) => {
            yearlyTotals.set(row.year, (yearlyTotals.get(row.year) || 0) + row.count);
          });

          const years = Array.from(new Set(rows.map((row) => row.year))).sort((a, b) => a - b);
          const datasets = raceOrder
            .filter((race) => rows.some((row) => row.race === race))
            .map((race) => {
              const color = raceColorMap[race] || 'rgba(107, 114, 128, 1)';
              return {
                label: raceLabelMap[race] || race,
                data: years.map((year) => {
                  const raceCount = rows
                    .filter((row) => row.year === year && row.race === race)
                    .reduce((sum, row) => sum + row.count, 0);
                  const total = yearlyTotals.get(year) || 0;
                  return total > 0 ? (raceCount / total) * 100 : 0;
                }),
                borderColor: color,
                backgroundColor: color.replace('1)', '0.15)'),
                borderWidth: 2,
                fill: true,
                tension: 0.15,
                pointRadius: 2,
                pointHoverRadius: 4,
              };
            });

          setCompositionSeries(rows);
          setChartData({
            labels: years.map((year) => year.toString()),
            datasets,
          });
        }

        setLoading(false);
      } catch (err: any) {
        console.error('PopulationTrendsSection: failed to load data', err);
        setError(err?.message || 'Failed to load demographic data');
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, geofips, location, resolveLocationForQuery, resolveLodesForQuery]);

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const chartTitle = `${tabTitleMap[activeTab]} - ${location}`;

    if (activeTab === 'by_race_pie') {
      if (compositionSeries.length === 0) return;
      const latestYear = compositionSeries.reduce((max, row) => Math.max(max, row.year), 0);
      const latestRows = compositionSeries.filter((row) => row.year === latestYear);
      const ordered = raceOrder.filter((race) => latestRows.some((r) => r.race === race));
      const pieData = {
        labels: ordered.map((r) => raceLabelMap[r] || r),
        datasets: [
          {
            data: ordered.map((race) => latestRows.filter((r) => r.race === race).reduce((s, r) => s + r.count, 0)),
            backgroundColor: ordered.map((race) => raceColorMap[race] || 'rgba(107, 114, 128, 1)'),
            borderWidth: 1,
          },
        ],
      };
      chartInstance.current = new Chart(ctx, {
        type: 'pie',
        data: pieData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { bottom: 24 } },
          plugins: {
            legend: { ...standardLegend, position: 'bottom' },
            title: standardTitle(`${chartTitle} (${latestYear})`),
            tooltip: {
              callbacks: {
                label: (context: any) => {
                  const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
                  const value = context.parsed || 0;
                  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                  return `${context.label}: ${value.toLocaleString()} (${pct}%)`;
                },
              },
            },
          },
        },
      });
      return () => {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }

    if (!chartData?.datasets?.length) return;

    const isByRaceLine = activeTab === 'by_race';

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(chartTitle),
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context: any) =>
                isByRaceLine
                  ? `${context.dataset.label}: ${(context.parsed.y || 0).toFixed(1)}%`
                  : `${context.dataset.label}: ${(context.parsed.y || 0).toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            stacked: isByRaceLine,
            title: { display: true, text: 'Year' },
          },
          y: isByRaceLine
            ? {
                stacked: true,
                beginAtZero: true,
                max: 100,
                title: { display: true, text: 'Share of Population (%)' },
                ticks: { callback: (value: any) => `${value}%` },
              }
            : {
                beginAtZero: true,
                title: { display: true, text: 'Workers' },
                ticks: { callback: (value: any) => value.toLocaleString() },
              },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [activeTab, chartData, location, compositionSeries]);

  const panelTitle = isUsingCountyData && countyName
    ? `Selected Demographic Characteristics (${countyName} County Data)`
    : 'Selected Demographic Characteristics';

  const getOverviewDescription = () => {
    if (activeTab === 'by_sex') {
      const dataNote = isUsingCountyData && countyName
        ? ` Workplace (employment) data for ${location} are shown at county level (${countyName}).`
        : '';
      return `This chart shows total workers by sex over time for ${location}, using workplace employment data (LODES).${dataNote} Demographic characteristics of households and workers inform equitable planning and service delivery.`;
    }
    if (activeTab === 'by_race_pie') {
      return `This pie chart shows racial composition for the latest available year for ${location}, using education-attainment race totals.`;
    }
    return `This chart shows racial composition over time for ${location} using annual race-group totals, displayed as a percentage share for each year. The equity balance percentile is computed for this geography (city or county) relative to California's non-white share.`;
  };

  const getRelevanceDescription = () => {
    if (activeTab === 'by_sex') {
      return 'Workforce composition by sex affects commuting patterns, transit demand, and equity-focused employment and training programs.';
    }
    return 'Racial composition trends help identify whether climate investments and adaptation strategies are reaching all communities equitably over time.';
  };

  const getEquityDescription = () => {
    if (activeTab === 'by_sex') {
      return 'Gender balance in the workforce is relevant to equitable access to jobs and transportation; tracking by sex supports targeted outreach and policy.';
    }
    return 'Racial composition trends provide context for equity-focused policy design, helping agencies prioritize outreach, funding, and implementation in underserved communities.';
  };

  const getMethodologyDescription = () => {
    if (activeTab === 'by_sex') {
      return 'Worker counts by sex come from LODES (LEHD Origin-Destination Employment Statistics), workplace geography, from 2009 onward.';
    }
    return 'Racial composition is computed from educational_attain_race rows (gender = Total), aggregated by race and year, then normalized to annual percentage shares. The equity balance percentile comes from the racial_equity_percentile_place_county materialized view (city or county, relative to California non-white share). Pie chart uses latest year only.';
  };

  /** Smart narrative for Key Insights using key metrics for the active chart. */
  const getKeyInsightsContent = (): React.ReactNode => {
    if (activeTab === 'by_sex' && sexSeries.length > 0) {
      const latestYear = Math.max(...sexSeries.map((r) => r.year));
      const latest = sexSeries.filter((r) => r.year === latestYear);
      const female = latest
        .filter((r) => /^(female|f)$/i.test(r.sex.trim()))
        .reduce((s, r) => s + r.total_worker, 0);
      const male = latest
        .filter((r) => /^(male|m)$/i.test(r.sex.trim()))
        .reduce((s, r) => s + r.total_worker, 0);
      const pctMore =
        male > 0 && female > 0 && female !== male
          ? female > male
            ? ((female - male) / male) * 100
            : ((male - female) / female) * 100
          : null;
      const ratioPhrase =
        pctMore !== null
          ? female > male
            ? `${pctMore.toFixed(1)}% more female than male workers`
            : `${pctMore.toFixed(1)}% more male than female workers`
          : 'roughly equal numbers of female and male workers';
      return (
        <p>
          In {latestYear}, {location} had {female.toLocaleString()} female workers and {male.toLocaleString()} male
          workers—{ratioPhrase}.
        </p>
      );
    }
    if (activeTab === 'by_race' || activeTab === 'by_race_pie') {
      if (topRacesByShare.length > 0) {
        const latestYear = compositionSeries.length ? Math.max(...compositionSeries.map((r) => r.year)) : null;
        const list = topRacesByShare.map(({ label, pct }) => `${label} (${pct.toFixed(1)}%)`).join(', ');
        return (
          <p>
            {latestYear != null && `In ${latestYear}, `}
            the top three racial groups of householders are {list}.
          </p>
        );
      }
      return null;
    }
    return null;
  };

  const chartContent = (
    <div style={{ position: 'relative', height: embedOnly ? '250px' : '400px' }}>
      <canvas ref={chartRef} />
    </div>
  );

  if (loading) {
    return (
      <ChartLayout
        scrollAnchorId="chart-population-trends"
        title="Selected Demographic Characteristics"
        chartType="line"
        activeView="overview"
        onViewChange={() => {}}
        chartContent={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading demographic data...</div>}
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
        scrollAnchorId="chart-population-trends"
        title="Selected Demographic Characteristics"
        chartType="line"
        activeView="overview"
        onViewChange={() => {}}
        chartContent={<div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>Error: {error}</div>}
      />
    );
  }

  if (embedOnly) {
    return chartContent;
  }

  return (
    <ChartLayout
      scrollAnchorId="chart-population-trends"
      title={panelTitle}
      chartType="line"
      activeView={activeView}
      onViewChange={(view: string) => setActiveView(view as 'overview' | 'relevance' | 'equity' | 'methodology')}
      keyMetrics={
        <KeyMetrics>
          {activeTab === 'by_sex' && sexSeries.length > 0 && (() => {
            const latestYear = Math.max(...sexSeries.map((r) => r.year));
            const latest = sexSeries.filter((r) => r.year === latestYear);
            // Only count rows that are strictly "Male" or "Female" so we don't double-count "Total" / "Male and Female" etc.
            const female = latest
              .filter((r) => /^(female|f)$/i.test(r.sex.trim()))
              .reduce((s, r) => s + r.total_worker, 0);
            const male = latest
              .filter((r) => /^(male|m)$/i.test(r.sex.trim()))
              .reduce((s, r) => s + r.total_worker, 0);
            const pctMore = male > 0 && female > 0 && female !== male
              ? (female > male
                ? ((female - male) / male) * 100
                : -((male - female) / female) * 100)
              : null;
            return (
              <>
                <KeyMetric
                  value={female.toLocaleString()}
                  label={`Female Workers (${latestYear})`}
                  color="#ec4899"
                />
                <KeyMetric
                  value={male.toLocaleString()}
                  label={`Male Workers (${latestYear})`}
                  color="#3b82f6"
                />
                {pctMore !== null && (
                  <KeyMetric
                    value={pctMore > 0 ? `${pctMore.toFixed(1)}% more female` : `${Math.abs(pctMore).toFixed(1)}% more male`}
                    label="Sex ratio"
                    color={pctMore > 0 ? '#ec4899' : '#3b82f6'}
                  />
                )}
              </>
            );
          })()}
          {activeTab === 'by_race' && topRacesByShare.length > 0 && (
            <>
              {topRacesByShare.map(({ race, label, pct }, i) => (
                <KeyMetric
                  key={race}
                  value={`${pct.toFixed(1)}%`}
                  label={label}
                  color={raceColorMap[race] || ['#7c3aed', '#8b5cf6', '#6d28d9'][i] || '#7c3aed'}
                />
              ))}
            </>
          )}
          {activeTab === 'by_race_pie' && topRacesByShare.length > 0 && (
            <>
              {topRacesByShare.map(({ race, label, pct }, i) => (
                <KeyMetric
                  key={race}
                  value={`${pct.toFixed(1)}%`}
                  label={label}
                  color={raceColorMap[race] || ['#7c3aed', '#8b5cf6', '#6d28d9'][i] || '#7c3aed'}
                />
              ))}
            </>
          )}
        </KeyMetrics>
      }
      chartSelector={{
        value: activeTab,
        onChange: (value: string) => setActiveTab(value as PopulationTab),
        options: [
          { value: 'by_sex', label: 'Workers by Sex' },
          { value: 'by_race_pie', label: 'Race Pie Chart' },
          { value: 'by_race', label: 'Householders by Race' },
        ],
      }}
      chartContent={chartContent}
      overviewContent={
        <OverviewContent title={tabTitleMap[activeTab]} description={getOverviewDescription()}>
          {getKeyInsightsContent()}
        </OverviewContent>
      }
      relevanceContent={<RelevanceContent title={`Relevance: ${tabTitleMap[activeTab]}`} description={getRelevanceDescription()} />}
      equityContent={<EquityContent title={`Equity: ${tabTitleMap[activeTab]}`} description={getEquityDescription()} />}
      methodologyContent={
        <MethodologyContent
          title="Methodology"
          description={getMethodologyDescription()}
          dataSources={
            activeTab === 'by_sex'
              ? ['Database table: lodes_employees_workplace_sex', 'Source: LODES (LEHD) workplace employment by sex', 'Years Available: 2009 onward']
              : ['Database table: educational_attain_race', 'Filters: gender = Total', 'Years Available: 2015-2023']
          }
          methodology={
            activeTab === 'by_sex'
              ? ['Query workplace employment by year and sex for selected geography', 'Display total workers by sex as line chart over time']
              : activeTab === 'by_race_pie'
                ? ['Aggregate race counts for latest year only', 'Display as pie chart with counts and percentages']
                : ['Aggregate race counts by year', 'Normalize each year to 100% for composition view', 'Display as stacked line chart']
          }
        />
      }
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
    />
  );
};

export default PopulationTrendsSection;
